import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { Flame, Trophy, CheckCircle2, Target, Smile } from "lucide-react";
import api from "@/lib/api";

const PIE_COLORS = ["#E9E604", "#3B82F6", "#8B5CF6", "#F59E0B", "#FF5722", "#EC4899", "#14B8A6"];
const MOOD_LABELS = ["Rough", "Meh", "Okay", "Good", "Great"];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-white py-20 text-center">Loading analytics…</div>;

  const { weekly, categories, trend, heatmap, stats, mood_week = [] } = data;
  const maxHeat = Math.max(1, ...heatmap.map((h) => h.count));
  const moodDays = mood_week.filter((m) => m.mood != null).length;
  const moodAvg = moodDays ? (mood_week.reduce((s, m) => s + (m.mood || 0), 0) / moodDays).toFixed(1) : null;

  return (
    <div className="space-y-8 pb-24 md:pb-8" data-testid="analytics-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2">Insights</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Your Analytics</h1>
        <p className="text-white mt-2">Track your consistency and spot your patterns.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={<Target className="w-5 h-5 text-blue-400" />} label="Total Habits" value={stats.total_habits} />
        <Stat icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />} label="Total Completions" value={stats.total_completions} />
        <Stat icon={<Flame className="w-5 h-5 text-orange-500" />} label="Best Streak" value={stats.best_streak} />
        <Stat icon={<Trophy className="w-5 h-5 text-amber-400" />} label="Today's Rate" value={`${stats.completion_rate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Weekly Completions">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#242417" vertical={false} />
              <XAxis dataKey="day" stroke="#FFFFFF" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#FFFFFF" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="completed" fill="#E9E604" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Category Breakdown">
          {categories.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={categories} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {categories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
    <Legend wrapperStyle={{ fontSize: 12, color: "#ffffff" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Weekly Mood Trend">
        {moodDays === 0 ? (
          <div className="h-[260px] flex flex-col items-center justify-center text-white gap-2">
            <Smile className="w-8 h-8 text-emerald-400" />
            <p className="text-sm">Log your mood in the Daily Check-in to see your week take shape.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-white">Avg this week</span>
              <span className="font-mono-stat text-xl font-bold text-emerald-400" data-testid="mood-avg">{moodAvg}</span>
              <span className="text-xs text-white">/ 5 · {MOOD_LABELS[Math.round(moodAvg) - 1]}</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={mood_week} margin={{ left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#242417" vertical={false} />
                <XAxis dataKey="day" stroke="#FFFFFF" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="#FFFFFF" fontSize={11} tickLine={false} axisLine={false}
                  domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} width={64}
                  tickFormatter={(v) => MOOD_LABELS[v - 1] || ""}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => [MOOD_LABELS[v - 1] || v, "Mood"]}
                />
                <Line
                  type="monotone" dataKey="mood" stroke="#E9E604" strokeWidth={2.5}
                  connectNulls={false}
                  dot={{ r: 4, fill: "#E9E604", stroke: "#0A0B07", strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: "#E9E604" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </Panel>

      <Panel title="30-Day Completion Trend">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#242417" vertical={false} />
            <XAxis dataKey="date" stroke="#FFFFFF" fontSize={11} tickLine={false} axisLine={false} interval={4} />
            <YAxis stroke="#FFFFFF" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Line type="monotone" dataKey="completed" stroke="#E9E604" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: "#E9E604" }} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Consistency Heatmap · Last 120 Days">
        <div className="flex flex-wrap gap-1.5" data-testid="heatmap-grid">
          {heatmap.map((h) => {
            const intensity = h.count === 0 ? 0 : h.count / maxHeat;
            const bg = h.count === 0 ? "#1a1d2b" : `rgba(194,195,28,${0.25 + intensity * 0.75})`;
            return <div key={h.date} title={`${h.date}: ${h.count} completed`} className="w-3.5 h-3.5 rounded-sm" style={{ background: bg }} />;
          })}
        </div>
        <div className="flex items-center gap-2 mt-4 text-xs text-white">
          <span>Less</span>
          {[0, 0.3, 0.55, 0.8, 1].map((v, i) => (
            <span key={i} className="w-3 h-3 rounded-sm" style={{ background: v === 0 ? "#1a1d2b" : `rgba(194,195,28,${0.25 + v * 0.75})` }} />
          ))}
          <span>More</span>
        </div>
      </Panel>
    </div>
  );
}

const tooltipStyle = { background: "#12141C", border: "1px solid #242417", borderRadius: 12, color: "#FFFFFF", fontSize: 13 };

const Panel = ({ title, children }) => (
  <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl">
    <h3 className="font-display text-lg font-semibold mb-5">{title}</h3>
    {children}
  </div>
);

const Stat = ({ icon, label, value }) => (
  <div className="p-5 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl">
    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center mb-3">{icon}</div>
    <div className="font-mono-stat text-2xl font-bold">{value}</div>
    <div className="text-xs text-white mt-1">{label}</div>
  </div>
);

const Empty = () => <div className="h-[260px] flex items-center justify-center text-white">No data yet</div>;
